import React, { useState } from 'react';
import "./faq.css";

const Faq = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [openItems, setOpenItems] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  const categories = [
    { id: 'all', name: 'All Questions' },
    { id: 'products', name: 'Products & Lenses' },
    { id: 'orders', name: 'Orders & Shipping' },
    { id: 'prescription', name: 'Prescriptions' },
    { id: 'warranty', name: 'Warranty & Returns' },
    { id: 'insurance', name: 'Insurance & Payments' }
  ];

  const faqData = [
    {
      id: 1,
      category: 'products',
      question: 'What types of lenses do you offer?',
      answer: 'Optica International offers a comprehensive range of lenses including single vision, bifocals, progressives, and specialized lenses for computer use. We also offer premium options like blue light filtering, anti-glare coating, photochromic lenses that darken in sunlight, and high-index lenses for stronger prescriptions.'
    },
    {
      id: 2,
      category: 'products',
      question: 'What is blue light protection and do I need it?',
      answer: 'Blue light protection filters out harmful blue light emitted from digital screens. It\'s recommended for anyone who spends significant time on computers, smartphones, or tablets. Our blue light lenses help reduce eye strain, improve sleep quality, and protect your eyes from potential long-term damage.'
    },
    {
      id: 3,
      category: 'products',
      question: 'Are your frames authentic designer brands?',
      answer: 'Yes, all our frames are 100% authentic. We are authorized retailers for all major designer brands including Ray-Ban, Oakley, Gucci, Prada, Versace, and many more. Each frame comes with its original authenticity cards and packaging.'
    },
    {
      id: 4,
      category: 'prescription',
      question: 'How do I read my prescription?',
      answer: 'Your prescription contains several measurements: SPH (sphere) indicates lens power, CYL (cylinder) measures astigmatism, Axis specifies astigmatism orientation, and ADD is for multifocal adds. If you need help interpreting your prescription, our opticians are available for consultation.'
    },
    {
      id: 5,
      category: 'prescription',
      question: 'Can I use an expired prescription?',
      answer: 'For safety reasons, we cannot fill prescriptions older than 2 years (1 year if you\'re under 18). This follows optometric guidelines ensuring your prescription still accurately reflects your vision needs. We recommend scheduling an eye exam if your prescription has expired.'
    },
    {
      id: 6,
      category: 'orders',
      question: 'How long does shipping take?',
      answer: 'Standard shipping takes 5-7 business days within the US. Express shipping (2-3 business days) and overnight options are available at checkout. International shipping typically takes 7-14 business days depending on the destination.'
    },
    {
      id: 7,
      category: 'orders',
      question: 'Can I track my order?',
      answer: 'Yes! Once your order ships, you\'ll receive a tracking number via email. You can also track your order through your account on our website or by contacting our customer service team.'
    },
    {
      id: 8,
      category: 'warranty',
      question: 'What is your warranty policy?',
      answer: 'All Optica International eyewear comes with a 2-year warranty against manufacturing defects. This includes frame breakage, lens coating defects, and hinge issues. We also offer a 30-day satisfaction guarantee – if you\'re not happy with your glasses, return them for a full refund.'
    },
    {
      id: 9,
      category: 'warranty',
      question: 'How do I return or exchange my glasses?',
      answer: 'Returns and exchanges are easy! Simply visit our Returns Portal, print your return label, and ship items back within 30 days of delivery. Glasses must be unworn and in original packaging. Refunds are processed within 5-7 business days of receiving the return.'
    },
    {
      id: 10,
      category: 'insurance',
      question: 'Do you accept vision insurance?',
      answer: 'Yes, we accept most major vision insurance plans including VSP, EyeMed, Davis Vision, and Spectera. You can enter your insurance information at checkout, and we\'ll verify your benefits and apply them to your purchase automatically.'
    },
    {
      id: 11,
      category: 'insurance',
      question: 'What payment methods do you accept?',
      answer: 'We accept all major credit cards (Visa, MasterCard, American Express, Discover), PayPal, Apple Pay, Google Pay, and flexible payment options like Klarna and Afterpay. We also offer financing options for qualified customers.'
    }
  ];

  const toggleItem = (id) => {
    setOpenItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const filteredFaqs = faqData.filter(item => {
    const matchesCategory = activeCategory === 'all' || item.category === activeCategory;
    const matchesSearch = item.question.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         item.answer.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="faq-container">
      <div className="faq-header">
        <h1>Frequently Asked Questions</h1>
        <p>Find answers to common questions about our products, prescriptions, and services</p>
      </div>

      <div className="faq-search">
        <input
          type="text"
          placeholder="Search your question..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="faq-search-input"
        />
      </div>

      <div className="faq-categories">
        {categories.map(category => (
          <button
            key={category.id}
            className={`category-btn ${activeCategory === category.id ? 'active' : ''}`}
            onClick={() => setActiveCategory(category.id)}
          >
            {category.name}
          </button>
        ))}
      </div>

      <div className="faq-list">
        {filteredFaqs.length > 0 ? (
          filteredFaqs.map(item => (
            <div key={item.id} className="faq-item">
              <button
                className={`faq-question ${openItems[item.id] ? 'active' : ''}`}
                onClick={() => toggleItem(item.id)}
              >
                <span>{item.question}</span>
                <span className="faq-icon">{openItems[item.id] ? '−' : '+'}</span>
              </button>
              {openItems[item.id] && (
                <div className="faq-answer">
                  <p>{item.answer}</p>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="faq-no-results">
            <p>No questions found matching your search. Please try different keywords or contact our support team.</p>
          </div>
        )}
      </div>

      <div className="faq-contact">
        <h3>Still have questions?</h3>
        <p>Our customer support team is here to help you 24/7</p>
        <div className="contact-options">
          <a href="mailto:support@opticainternational.com" className="contact-btn">Email Us</a>
          <a href="tel:+18005551234" className="contact-btn">Call Us</a>
          <a href="/live-chat" className="contact-btn">Live Chat</a>
        </div>
      </div>
    </div>
  );
};

export default Faq;